import { FC, useCallback, useEffect, useRef, useState } from "react";
import { ChatState } from "../../types";
import { Column, Row } from "../../../../components/containers";
import {
  useAlertStore,
  useChatStore,
  useSocketStore,
  useThemeStore,
} from "../../../../stores";
import {
  AlignItems,
  JustifyContent,
} from "../../../../components/containers/types";
import { ChatButton, ChatInput } from "../../components";
import {
  DocumentIcon,
  PlusWithoutCircleIcon,
  SendIcon,
} from "../../../../assets/icons";
import { CHAT_PAGE_ROOM_IDX, SIDE_PANEL_ROOM_IDX } from "../../../../constants";
import ChatBodyFooterFiles from "./chat-body-footer-files";
import ChatBodyFooterEditing from "./chat-body-footer-editing";

const ChatBodyFooter: FC<ChatState> = ({
  isSidePanel,
  editingChat,
  setEditingChat,
}) => {
  const theme = useThemeStore((s) => s.theme);
  const sendChat = useChatStore((s) => s.sendChat);
  const sockDispatch = useSocketStore((s) => s.sockDispatch);
  const selectedRooms = useChatStore((s) => s.selectedRooms);
  const pushAlert = useAlertStore((s) => s.pushAlert);

  const [text, setText] = useState<string>("");
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<FileList | null>(null);
  const [delFiles, setDelFiles] = useState<string[]>([]);

  const inpRef = useRef<HTMLInputElement>(null);

  const onSend = useCallback((): void => {
    if (!editingChat) {
      if (!text && !files) return;
      sendChat(
        isSidePanel ? SIDE_PANEL_ROOM_IDX : CHAT_PAGE_ROOM_IDX,
        text,
        files
      );
      setText("");
      setFiles(null);
    } else {
      if (!text) return pushAlert("متن نمی تواند خالی باشد!", "error");
      sockDispatch("update_message", {
        id: editingChat._id.$oid,
        content: text,
        del_files: delFiles,
        chat:
          selectedRooms[isSidePanel ? SIDE_PANEL_ROOM_IDX : CHAT_PAGE_ROOM_IDX]
            ?._id.$oid || "",
      });
      setText("");
      setFiles(null);
      if (setEditingChat) setEditingChat(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingChat, text, files, isSidePanel, delFiles, selectedRooms]);

  useEffect(() => {
    if (editingChat) {
      setText(editingChat.content);
    } else {
      setText("");
    }
    setDelFiles([]);
  }, [editingChat]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "Enter":
          if (!e.shiftKey) {
            e.preventDefault();
            onSend();
          }
          break;
      }
    },
    [onSend]
  );

  useEffect(() => {
    if (isFocus) {
      window.addEventListener("keydown", onKeyDown);
    } else {
      window.removeEventListener("keydown", onKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onKeyDown, isFocus]);

  const onDocumentClick = (): void => {
    if (!inpRef.current) return;
    setIsOpen(false);
    if (editingChat) return;
    inpRef.current.click();
  };

  return (
    <Row
      style={{
        width: "calc(100% - 16px)",
        padding: 8,
        borderTop: `1px solid ${theme.iconLowOpacity}`,
        position: "relative",
      }}
      justifyContent={JustifyContent.Between}
      alignItems={AlignItems.End}
      gap={8}
    >
      <ChatBodyFooterEditing
        delFiles={delFiles}
        setDelFiles={setDelFiles}
        editingChat={editingChat}
        setEditingChat={setEditingChat}
        isSidePanel={isSidePanel}
      />

      <ChatBodyFooterFiles
        files={files}
        setFiles={setFiles}
        isSidePanel={isSidePanel}
      />

      <Row
        gap={8}
        style={{ width: "100%", position: "relative" }}
        alignItems={AlignItems.End}
      >
        {isOpen ? (
          <Column
            style={{
              position: "absolute",
              right: 0,
              bottom: "calc(100% + 8px)",
            }}
            gap={10}
          >
            <ChatButton onClick={onDocumentClick} Icon={DocumentIcon} />
          </Column>
        ) : (
          <></>
        )}

        <input
          multiple
          type={"file"}
          style={{ display: "none" }}
          ref={inpRef}
          onChange={(e) => setFiles(e.target.files)}
        />

        <ChatButton
          onClick={() => setIsOpen((prev) => !prev)}
          Icon={PlusWithoutCircleIcon}
          style={{ minWidth: 30 }}
        />

        <ChatInput
          value={text}
          setValue={setText}
          isSidePanel={isSidePanel}
          props={{
            placeholder: "این جا بنویسید",
            onFocus: () => setIsFocus(true),
            onBlur: () => setIsFocus(false),
          }}
          style={{ lineHeight: "30px" }}
        />
      </Row>

      <ChatButton onClick={onSend} Icon={SendIcon} style={{ minWidth: 30 }} />
    </Row>
  );
};

export default ChatBodyFooter;
